import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { findMatchingInvitees } from '../utils/stringMatch';
import { useApp } from '../context/AppContext';
import AddToCalendar from '../components/AddToCalendar';
import '../styles/Pages.css';

const ADMIN_PHONE = '(716) 908-0639';
const ADMIN_PHONE_TEL = 'tel:+17169080639';
const API = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const RSVPPage = () => {
  const { invites, submitRSVP, getRSVPByInviteeId, settings } = useApp();
  const { rsvpDeadline, enforceDeadline } = settings;
  const navigate = useNavigate();

  const [step, setStep] = useState('verify');
  const [inputName, setInputName] = useState('');
  const [matchedInvitees, setMatchedInvitees] = useState([]);
  const [verifiedInvitee, setVerifiedInvitee] = useState(null);
  const [isUpdate, setIsUpdate] = useState(false);
  const [previousRSVP, setPreviousRSVP] = useState(null);

  const [formData, setFormData] = useState({
    status: '',
    team: '',
    bringingItem: '',
    additionalNotes: '',
    phone: '',
    phoneConsent: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  // Send-a-message state (not-found step)
  const [messageText, setMessageText] = useState('');
  const [messageName, setMessageName] = useState('');
  const [messageSent, setMessageSent] = useState(false);
  const [messageSending, setMessageSending] = useState(false);

  const rsvpClosed = enforceDeadline && new Date() > rsvpDeadline;

  const handleNameVerify = (e) => {
    e.preventDefault();
    const matches = findMatchingInvitees(inputName, invites, 0.7);
    if (matches.length === 0) {
      setMessageName(inputName);
      setMessageText('');
      setMessageSent(false);
      setStep('not-found');
    } else {
      setMatchedInvitees(matches);
      setStep('select');
    }
  };

  const selectInvitee = (invitee) => {
    setVerifiedInvitee(invitee);
    const existing = getRSVPByInviteeId(invitee.id);
    if (existing) {
      setIsUpdate(true);
      setPreviousRSVP(existing);
      setFormData({
        status: existing.status || '',
        team: existing.team === 'Cheerleader' ? '' : (existing.team || ''),
        bringingItem: existing.bringingItem || '',
        additionalNotes: existing.additionalNotes || '',
      });
    }
    setStep('form');
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setSubmitError(null);
    try {
      await submitRSVP({
        inviteeId: verifiedInvitee.id,
        status: formData.status,
        team: formData.team,
        bringingItem: formData.bringingItem,
        additionalNotes: formData.additionalNotes,
        phone: (formData.phone && formData.phoneConsent) ? formData.phone : undefined,
      });
      setStep('submitted');
    } catch {
      setSubmitError('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const statusDisplayLabel = (s) => ({
    playing: 'Playing',
    attending: 'Attending (Dinner / Cheering)',
    unavailable: "Can't make it",
  }[s] || s);

  if (rsvpClosed) {
    return (
      <div className="page-container">
        <div className="page-content">
          <div className="error-message">
            <h2>RSVPs Are Closed</h2>
            <p>The RSVP deadline was <strong>{rsvpDeadline.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</strong>.</p>
            <p>If you need to make a change, please contact us directly:</p>
            <div className="contact-info">
              <a href={ADMIN_PHONE_TEL} className="phone-number">{ADMIN_PHONE}</a>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (step === 'verify') {
    return (
      <div className="page-container">
        <div className="page-content">
          <h2>RSVP to CanAm Event</h2>
          <div className="rsvp-form">
            <div className="info-section">
              <p>Please enter your full name as it appears on your invitation to continue.</p>
              <p className="update-hint">Already RSVP'd? Enter your name again to update your response.</p>
              <p className="update-hint">RSVP deadline: <strong>{rsvpDeadline.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</strong></p>
            </div>
            <form onSubmit={handleNameVerify}>
              <div className="form-group">
                <label htmlFor="inputName">Your Full Name *</label>
                <input
                  type="text"
                  id="inputName"
                  value={inputName}
                  onChange={(e) => setInputName(e.target.value)}
                  required
                  placeholder="First Last"
                  autoFocus
                />
              </div>
              <button type="submit" className="submit-btn">Continue</button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  if (step === 'select') {
    return (
      <div className="page-container">
        <div className="page-content">
          <h2>Select Your Name</h2>
          <div className="rsvp-form">
            <div className="info-section">
              <p>{matchedInvitees.length === 1 ? `Is this you?` : `We found multiple possible matches for "${inputName}". Please select your name:`}</p>
            </div>
            <div className="name-selection">
              {matchedInvitees.map((invitee) => (
                <button key={invitee.id} className="name-option" onClick={() => selectInvitee(invitee)}>
                  <span className="name-option-text">{invitee.name}</span>
                </button>
              ))}
            </div>
            <button
              onClick={() => { setStep('verify'); setInputName(''); setMatchedInvitees([]); }}
              className="back-btn"
            >
              ← Try Different Name
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (step === 'not-found') {
    const handleSendMessage = async (e) => {
      e.preventDefault();
      setMessageSending(true);
      try {
        await fetch(`${API}/messages`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ name: messageName, typedName: inputName, message: messageText }),
        });
        setMessageSent(true);
      } catch {
        alert('Failed to send message. Please try calling instead.');
      } finally {
        setMessageSending(false);
      }
    };

    return (
      <div className="page-container">
        <div className="page-content">
          <div className="error-message">
            <h2>Name Not Found</h2>
            <p>We couldn't find <strong>"{inputName}"</strong> on our invite list.</p>
            <p>Some invites are listed by household — try searching just your <strong>last name</strong>, or a single first name. Still stuck? It could be a typo — give it another try, or send us a message below.</p>
            <button onClick={() => { setStep('verify'); setInputName(''); }} className="submit-btn" style={{ marginBottom: '2rem' }}>
              ← Try Again
            </button>
          </div>

          <div className="rsvp-form" style={{ marginTop: '1.5rem' }}>
            {messageSent ? (
              <div style={{ textAlign: 'center', padding: '2rem 0' }}>
                <h3 style={{ color: '#2e7d32' }}>Message Sent!</h3>
                <p>We'll get back to you shortly.</p>
              </div>
            ) : (
              <>
                <h3 style={{ marginTop: 0, marginBottom: '1rem', color: '#333' }}>Send Us a Message</h3>
                <form onSubmit={handleSendMessage}>
                  <div className="form-group">
                    <label>Your Name</label>
                    <input
                      type="text"
                      value={messageName}
                      onChange={e => setMessageName(e.target.value)}
                      required
                      placeholder="Your full name"
                    />
                  </div>
                  <div className="form-group">
                    <label>Message</label>
                    <textarea
                      rows="3"
                      value={messageText}
                      onChange={e => setMessageText(e.target.value)}
                      required
                      placeholder="e.g. I think I'm on the list as Bob — can you check?"
                    />
                  </div>
                  <button type="submit" className="submit-btn" disabled={messageSending}>
                    {messageSending ? 'Sending…' : 'Send Message'}
                  </button>
                </form>
                <div className="contact-info" style={{ marginTop: '1.5rem' }}>
                  <p><strong>Or call/text directly:</strong></p>
                  <a href={ADMIN_PHONE_TEL} className="phone-number">{ADMIN_PHONE}</a>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (step === 'submitted') {
    const resolvedTeam = formData.status === 'attending' ? 'Cheerleader' : formData.team;
    return (
      <div className="page-container">
        <div className="page-content">
          <div className="success-message">
            <h2>{isUpdate ? 'RSVP Updated!' : 'RSVP Submitted!'}</h2>
            <p>Thank you, <strong>{verifiedInvitee.name}</strong>!</p>

            <div className="rsvp-summary-card">
              <h3>Your Response</h3>
              <div className="summary-row">
                <span className="summary-key">Status</span>
                <span className="summary-val-text">{statusDisplayLabel(formData.status)}</span>
              </div>
              {resolvedTeam && (
                <div className="summary-row">
                  <span className="summary-key">Team</span>
                  <span className="summary-val-text">{resolvedTeam}</span>
                </div>
              )}
              {formData.bringingItem && (
                <div className="summary-row">
                  <span className="summary-key">Bringing</span>
                  <span className="summary-val-text">{formData.bringingItem}</span>
                </div>
              )}
              {formData.additionalNotes && (
                <div className="summary-row">
                  <span className="summary-key">Notes</span>
                  <span className="summary-val-text">{formData.additionalNotes}</span>
                </div>
              )}
            </div>

            {formData.status !== 'unavailable' && <AddToCalendar />}

            {formData.status !== 'unavailable' && (
              <div style={{ marginTop: '1.5rem', textAlign: 'center' }}>
                <a href="/contact.vcf" className="save-contact-btn">📇 Save “CanAm Games” to Contacts</a>
                <p style={{ margin: '0.5rem 0 0', fontSize: '0.85rem', color: '#888' }}>
                  So you'll recognize our event texts from (716) 249-3421.
                </p>
              </div>
            )}

            <p className="update-reminder">Need to make a change? Come back any time and enter your name again.</p>

            <div className="success-actions">
              <button className="success-action-btn" onClick={() => navigate('/guests')}>
                👀 See who else is coming
              </button>
              <button className="success-action-btn" onClick={() => navigate('/video')}>
                🎬 Check out the event video
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // RSVP Form
  return (
    <div className="page-container">
      <div className="page-content">
        <h2>{isUpdate ? 'Update Your RSVP' : 'RSVP Form'}</h2>
        <p className="verified-name">
          Welcome, <strong>{verifiedInvitee.name}</strong>!
        </p>

        {isUpdate && previousRSVP && (
          <div className="update-banner">
            <strong>Updating your previous response.</strong> Your current response is shown below — change anything you'd like and resubmit.
          </div>
        )}

        <form onSubmit={handleSubmit} className="rsvp-form">
          <div className="form-group">
            <label>How will you be joining us? *</label>
            <div className="radio-group">
              <label className="radio-label">
                <input
                  type="radio"
                  name="status"
                  value="playing"
                  checked={formData.status === 'playing'}
                  onChange={handleChange}
                  required
                />
                I'll be playing!
              </label>
              <label className="radio-label">
                <input
                  type="radio"
                  name="status"
                  value="attending"
                  checked={formData.status === 'attending'}
                  onChange={handleChange}
                />
                I'm coming for dinner / cheering
              </label>
              <label className="radio-label">
                <input
                  type="radio"
                  name="status"
                  value="unavailable"
                  checked={formData.status === 'unavailable'}
                  onChange={handleChange}
                />
                Sorry, I can't make it
              </label>
            </div>
          </div>

          {formData.status === 'playing' && (
            <div className="form-group">
              <label>Choose Your Team *</label>
              <div className="team-selection">
                <label className={`team-option team-usa ${formData.team === 'USA' ? 'selected' : ''}`}>
                  <input
                    type="radio"
                    name="team"
                    value="USA"
                    checked={formData.team === 'USA'}
                    onChange={handleChange}
                    required
                  />
                  <span className="team-name">Team USA</span>
                </label>
                <label className={`team-option team-canada ${formData.team === 'Canada' ? 'selected' : ''}`}>
                  <input
                    type="radio"
                    name="team"
                    value="Canada"
                    checked={formData.team === 'Canada'}
                    onChange={handleChange}
                  />
                  <span className="team-name">Team Canada</span>
                </label>
              </div>
            </div>
          )}

          {(formData.status === 'playing' || formData.status === 'attending') && (
            <>
              <div className="form-group">
                <label htmlFor="bringingItem">Bringing anything? <span style={{ color: '#aaa', fontWeight: 400, fontSize: '0.9rem' }}>(totally optional)</span></label>
                <input
                  type="text"
                  id="bringingItem"
                  name="bringingItem"
                  value={formData.bringingItem}
                  onChange={handleChange}
                  placeholder="Not necessary, but if you'd like — drinks, snacks, dessert…"
                />
              </div>

              {!verifiedInvitee.hasPhone && (
                <div className="form-group">
                  <label htmlFor="phone">Phone Number <span style={{ color: '#aaa', fontWeight: 400, fontSize: '0.9rem' }}>(optional)</span></label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={e => {
                      const digits = e.target.value.replace(/\D/g, '').slice(0, 10);
                      let formatted = digits;
                      if (digits.length > 6) formatted = `(${digits.slice(0,3)}) ${digits.slice(3,6)}-${digits.slice(6)}`;
                      else if (digits.length > 3) formatted = `(${digits.slice(0,3)}) ${digits.slice(3)}`;
                      setFormData(prev => ({ ...prev, phone: formatted }));
                    }}
                    placeholder="(716) 555-0000"
                  />
                  <label style={{ display: 'flex', alignItems: 'flex-start', gap: '0.55rem', marginTop: '0.65rem', fontSize: '0.8rem', color: '#777', lineHeight: 1.5, cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={formData.phoneConsent}
                      onChange={e => setFormData(prev => ({ ...prev, phoneConsent: e.target.checked }))}
                      style={{ marginTop: '0.15rem', flexShrink: 0, width: '16px', height: '16px', cursor: 'pointer' }}
                    />
                    <span>
                      I agree to receive event text messages from CanAm Games (RSVP confirmation &amp; reminders)
                      at this number. Msg frequency varies. Msg &amp; data rates may apply. Reply STOP to opt out,
                      HELP for help. See our{' '}
                      <a href="/privacy" target="_blank" rel="noopener noreferrer" style={{ color: '#3c5bc4' }}>Privacy Policy</a>
                      {' '}&amp;{' '}
                      <a href="/terms" target="_blank" rel="noopener noreferrer" style={{ color: '#3c5bc4' }}>Terms</a>.
                    </span>
                  </label>
                </div>
              )}

              <div className="form-group">
                <label htmlFor="additionalNotes">Additional Notes</label>
                <textarea
                  id="additionalNotes"
                  name="additionalNotes"
                  value={formData.additionalNotes}
                  onChange={handleChange}
                  rows="3"
                  placeholder="Any dietary restrictions, song requests, or anything else we should know?"
                />
              </div>
            </>
          )}

          {submitError && (
            <p style={{ color: '#c62828', marginBottom: '0.75rem', fontWeight: 500 }}>{submitError}</p>
          )}
          <button type="submit" className="submit-btn" disabled={submitting}>
            {submitting ? 'Saving…' : (isUpdate ? 'Update RSVP' : 'Submit RSVP')}
          </button>
        </form>
      </div>
    </div>
  );
};

export default RSVPPage;
